import { assertSportsbookHistoryRow } from '../contracts/sportsbookHistoryResponse.contract.js';
import {
  SPORTSBOOK_HISTORY_OPTIMISTIC_EVENT,
  SPORTSBOOK_HISTORY_OPTIMISTIC_MAX_ROWS,
  SPORTSBOOK_HISTORY_OPTIMISTIC_STORAGE_KEY,
  SPORTSBOOK_HISTORY_OPTIMISTIC_TTL_MS,
} from '../policies/sportsbookHistory.policy.js';

const resolveStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const safeRow = (row) => {
  try {
    return assertSportsbookHistoryRow(row);
  } catch {
    return null;
  }
};

const readEntries = ({ storage = resolveStorage(), now = Date.now(), maxRows = SPORTSBOOK_HISTORY_OPTIMISTIC_MAX_ROWS } = {}) => {
  if (!storage) return [];
  try {
    const parsed = JSON.parse(storage.getItem(SPORTSBOOK_HISTORY_OPTIMISTIC_STORAGE_KEY) || '[]');
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((entry) => typeof entry?.expiresAt === 'number' && entry.expiresAt > now)
      .map((entry) => ({ ...entry, row: safeRow(entry.row) }))
      .filter((entry) => entry.row)
      .slice(0, maxRows);
  } catch {
    return [];
  }
};

const writeEntries = (entries, storage = resolveStorage()) => {
  if (!storage) return false;
  try {
    storage.setItem(SPORTSBOOK_HISTORY_OPTIMISTIC_STORAGE_KEY, JSON.stringify(entries));
    return true;
  } catch {
    return false;
  }
};

export const readOptimisticSportsbookHistoryRows = (options = {}) =>
  readEntries(options).map((entry) => entry.row);

export const storeOptimisticSportsbookHistoryRow = (row, {
  storage = resolveStorage(),
  now = Date.now(),
  ttlMs = SPORTSBOOK_HISTORY_OPTIMISTIC_TTL_MS,
  maxRows = SPORTSBOOK_HISTORY_OPTIMISTIC_MAX_ROWS,
} = {}) => {
  const validRow = safeRow(row);
  if (!validRow || !storage) return null;
  const entries = readEntries({ storage, now, maxRows })
    .filter((entry) => entry.row.history_id !== validRow.history_id);
  const nextEntries = [
    { row: validRow, expiresAt: now + ttlMs },
    ...entries,
  ].slice(0, maxRows);
  return writeEntries(nextEntries, storage) ? validRow : null;
};

export const reconcileOptimisticSportsbookHistoryRows = (serverRows, options = {}) => {
  const storage = options.storage ?? resolveStorage();
  const serverIds = new Set(serverRows.map((row) => row.history_id));
  const remainingEntries = readEntries({ ...options, storage })
    .filter((entry) => !serverIds.has(entry.row.history_id));
  return writeEntries(remainingEntries, storage)
    ? remainingEntries.map((entry) => entry.row)
    : [];
};

export const mergeOptimisticSportsbookHistoryRows = (optimisticRows, serverRows) => {
  const seen = new Set();
  return [...optimisticRows, ...serverRows].filter((row) => {
    if (seen.has(row.history_id)) return false;
    seen.add(row.history_id);
    return true;
  });
};

export const dispatchOptimisticSportsbookHistoryRow = (row) => {
  const storedRow = storeOptimisticSportsbookHistoryRow(row);
  if (!storedRow || typeof window === 'undefined' || typeof window.CustomEvent !== 'function') return false;
  window.dispatchEvent(new window.CustomEvent(SPORTSBOOK_HISTORY_OPTIMISTIC_EVENT, {
    detail: { historyRow: storedRow },
  }));
  return true;
};
